import { createRouter } from '@tanstack/react-router'
import { ScreenLoading } from '@/shared/ui'
import type { QueryClient } from '@tanstack/react-query'
import { rootRoute } from './root-route'
import { indexRoute, shellRoute } from './routes'
import { loginRoute } from './routes/login'
import { dashboardRoute } from './routes/dashboard'
import { chessRoute } from './routes/chess'
import { bookingsRoute } from './routes/bookings'
import { tasksRoute } from './routes/tasks'
import { inboxRoute } from './routes/inbox'
import { studioRoute, studioIndexRoute } from './routes/studio'
import { noAccessRoute, noCompaniesRoute } from './routes/misc'
import { isSelectionChange } from './scroll-policy'

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  noAccessRoute,
  noCompaniesRoute,
  shellRoute.addChildren([
    dashboardRoute,
    chessRoute,
    bookingsRoute,
    tasksRoute,
    inboxRoute,
    studioIndexRoute,
    studioRoute,
  ]),
])

export function buildRouter(queryClient: QueryClient) {
  let selectionChange = false
  const router = createRouter({
    routeTree,
    context: { queryClient },
    // Префетч по наведению — но только если курсор задержался.
    // Проводка через навигацию не должна порождать запросы.
    defaultPreload: 'intent',
    defaultPreloadDelay: 450,
    defaultPreloadStaleTime: 30_000,

    // Показываем, только если ответ дольше 200 мс — иначе мигание на быстром ответе.
    defaultPendingMs: 200,
    defaultPendingMinMs: 300,

    // Без этого компонента холодный старт БЕЗ СЕТИ давал белую страницу.
    //
    // Единственный асинхронный `beforeLoad` — охранник сессии на `shellRoute` — ждёт `GET /me`.
    // Офлайн запрос не отклоняется, а СТАВИТСЯ НА ПАУЗУ (`networkMode: 'online'` — дефолт
    // TanStack Query): промис висит бессрочно, пока не вернётся сеть. `defaultPendingMs` был
    // задан, а показывать было нечего — роутер отрисовывал пустоту.
    //
    // Оффлайн-баннер при этом виден: он смонтирован вне роутера (`App.tsx`), так что человек
    // увидит «нет соединения» поверх этого экрана и поймёт, чего ждёт.
    defaultPendingComponent: ScreenLoading,

    /*
     * ⚠️ СМЕНА ВЫБРАННОГО ОБЪЕКТА — НЕ СМЕНА СТРАНИЦЫ (`scroll-policy.ts`), и прокрутку она не трогает
     * вовсе: `false` пропускает разом перенос, восстановление и сброс (`scroll-restoration.js`,
     * «onRendered»). Без этого открытие брони сбрасывает список под модалкой наверх, а на соседний
     * объект из кеша роутер переносит прокрутку прежнего — лента переписки не доходит до низа ровно
     * на разницу высот. Прокрутку объекта ставит его владелец, и на «назад» роутер её не
     * возвращает — та же цена, что у смены страниц ниже.
     */
    scrollRestoration: () => !selectionChange,

    /*
     * ⚠️ БЕЗ ЭТОЙ СТРОКИ ПРОКРУТКА ПЕРЕЕЗЖАЕТ МЕЖДУ ЭКРАНАМИ. Восстановление умеет две разные вещи:
     * вернуть прокрутку при возврате назад и обнулить её при переходе вперёд. Второе оно делает
     * через `window.scrollTo({top: 0})` плюс элементы из этого списка — а с тех пор, как прокрутка
     * уехала внутрь `AppShell.content` (оболочка ограничена высотой экрана), вызов по окну не
     * делает НИЧЕГО: документ не прокручивается. Обёртка же одна на все маршруты и живёт между
     * ними, поэтому без явного обнуления получала семантику боковой панели: пролистал «Брони» и
     * перешёл на «Сводку» — она открывалась с той же середины.
     *
     * ⚠️ ЦЕНА: возврат «назад» больше НЕ возвращает на прежнее место. Это развилка самой библиотеки:
     * восстановление и сброс лежат в одной ветке (`scroll-restoration.js`, «onRendered») — сначала
     * применяются записи из кеша, затем идёт сброс, если не было восстановлено окно И нет хеша.
     * Окна у нас не бывает никогда, поэтому сброс идёт последним и затирает восстановленную позицию.
     * Выбрать можно одно из двух, и переезд чужой прокрутки — неправда на экране, а несохранённая
     * позиция лишь неудобство.
     *
     * Измерено на 1440×700: до правки оболочки 250 → 0 → 250, сейчас 250 → 0 → 0.
     *
     * ⚠️ Ловится только на окне, в которое страница НЕ влезает: где содержимое короче экрана,
     * `scrollTop` и так ноль. Проверять на низком окне, а не на большом.
     */
    scrollToTopSelectors: ['[data-scroll-restoration-id="app-content"]'],
  })
  // Решаем на `onBeforeLoad`: у него та же пара «последнее отрисованное → цель», что получит
  // `onRendered`, — и у «назад» браузера тоже, куда `resetScroll` не передать.
  router.subscribe('onBeforeLoad', ({ fromLocation, toLocation }) => {
    selectionChange = isSelectionChange(fromLocation, toLocation)
  })
  return router
}

export type AppRouter = ReturnType<typeof buildRouter>

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter
  }
}
